/**
 * The editor schema version, per `docs/spec/collaboration.md` §6. It names the shape of the
 * document the editor can represent faithfully, and the collaboration server refuses clients
 * whose **major** is behind its own — an older editor would silently drop nodes it does not
 * understand and then save the loss back.
 *
 * It lives in core because both ends of the socket have to agree on it: the browser sends it
 * as a query parameter and the server compares it against this same constant.
 */
export const EDITOR_VERSION = "1.0.0";

export interface Semver {
  major: number;
  minor: number;
  patch: number;
}

const SEMVER = /^(\d+)\.(\d+)\.(\d+)(?:[-+].*)?$/;

/** Parses `major.minor.patch`, ignoring any pre-release or build suffix. Null when malformed. */
export function parseSemver(value: string): Semver | null {
  const match = SEMVER.exec(value.trim());
  if (!match) {
    return null;
  }
  const [, major, minor, patch] = match;
  if (major === undefined || minor === undefined || patch === undefined) {
    return null;
  }
  return { major: Number(major), minor: Number(minor), patch: Number(patch) };
}

/**
 * Orders two version strings: negative when `a` is older, positive when it is newer, zero when
 * they are equal. An unparseable version is older than every parseable one, and two
 * unparseable ones compare equal.
 */
export function compareSemver(a: string, b: string): number {
  const left = parseSemver(a);
  const right = parseSemver(b);
  if (!left || !right) {
    return left ? 1 : right ? -1 : 0;
  }
  return left.major - right.major || left.minor - right.minor || left.patch - right.patch;
}

/**
 * The newer of two versions, with null losing to a non-null. This is the reconciliation
 * `docs/spec/collaboration.md` §6 requires on every store: the column records the newest
 * editor that has ever touched the document.
 */
export function maxSemver(a: string | null, b: string | null): string | null {
  if (a === null) {
    return b;
  }
  if (b === null) {
    return a;
  }
  return compareSemver(a, b) >= 0 ? a : b;
}

/**
 * Whether a client announcing `clientVersion` may join. Equal or greater majors are accepted;
 * a missing or malformed version never is (§6: the connection is refused with `4999`).
 */
export function isEditorVersionSupported(
  clientVersion: string | null | undefined,
  serverVersion: string = EDITOR_VERSION,
): boolean {
  if (clientVersion === null || clientVersion === undefined || clientVersion === "") {
    return false;
  }
  const client = parseSemver(clientVersion);
  const server = parseSemver(serverVersion);
  if (!client || !server) {
    return false;
  }
  return client.major >= server.major;
}
